import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  XMarkIcon,
  CheckIcon,
  FunnelIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

import MainLayout from '../components/Layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import useDebounce from '../hooks/useDebounce';
import useSubmissionProtection from '../hooks/useSubmissionProtection';
import { 
  Input, 
  Select, 
  Button, 
  Modal, 
  DataTable, 
  showToast,
  ConfirmDialog
} from '../components/UI';
import { quotationService } from '../services/quotationService';
import { Quotation, QuotationFilters } from '../types/quotation';
import enquiryService, { Enquiry } from '../services/enquiryService';
import { itemService } from '../services/itemService';
import { Item } from '../types/item';
import api from '../services/api';

interface TableParams {
  page: number;
  limit: number;
  search: string;
  enquiry_id?: number;
  is_active?: string;
}

interface QuotationFormData {
  enquiry_id: number;
  quote_date: string;
  no_of_feet: number;
  sub_total: number;
  net_amount: number;
  remarks?: string;
  quotationDetails: Array<{
    particulars: string;
    qty: number;
    rate: number;
    amount: number;
    item_id?: number;
    is_auto_generated?: boolean;
  }>;
}

// Helper function to sort quotation details by sl_no
const sortQuotationDetailsBySlNo = (details: any[]) => {
  return details.sort((a, b) => {
    // Custom sort for sl_no (handles 1.1, 1.2, etc.)
    const parseSlNo = (slNo: string | number) => {
      const slNoStr = String(slNo);
      const parts = slNoStr.split('.');
      return parts.length === 2 ? [parseInt(parts[0]), parseInt(parts[1])] : [parseInt(parts[0]), 0];
    };
    const [aMain, aSub] = parseSlNo(a.sl_no);
    const [bMain, bSub] = parseSlNo(b.sl_no);
    return aMain !== bMain ? aMain - bMain : aSub - bSub;
  });
};

// Helper function to process quotation details (auto and manual items)
const processQuotationDetails = (quotationDetails: any[] | undefined) => {
  if (!quotationDetails || quotationDetails.length === 0) {
    return { autoGeneratedItems: [], manualGeneratedItems: [] };
  }
  
  // Filter and sort auto-generated items
  const autoGeneratedItems = sortQuotationDetailsBySlNo(
    quotationDetails.filter(detail => detail.is_auto_generated)
  );
  
  // Filter and sort manual items  
  const manualGeneratedItems = sortQuotationDetailsBySlNo(
    quotationDetails.filter(detail => !detail.is_auto_generated)
  );
  
  return { autoGeneratedItems, manualGeneratedItems };
};

const QuotationManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState<Quotation | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showActionMenu, setShowActionMenu] = useState<number | null>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; quotation: Quotation | null }>({
    show: false,
    quotation: null
  });
  const [deleting, setDeleting] = useState(false);
  const [cloningQuotation, setCloningQuotation] = useState<Quotation | null>(null);
  const [sharingQuotationId, setSharingQuotationId] = useState<number | null>(null);

  // Form-specific states
  const [enquiries] = useState<Enquiry[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);
  const [autoItems, setAutoItems] = useState<any[]>([]);
  const [manualItems, setManualItems] = useState<any[]>([]);
  const [lastCalculatedFeet, setLastCalculatedFeet] = useState<number>(0);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [formData, setFormData] = useState<{
    enquiry_id: number;
    quote_date: string;
    no_of_feet: number;
    sub_total: number;
    net_amount: number;
    remarks?: string;
  }>({
    enquiry_id: 0,
    quote_date: new Date().toISOString().split('T')[0],
    no_of_feet: 0,
    sub_total: 0,
    net_amount: 0,
    remarks: ''
  });
  
  // Pagination and filter states
  const [tableParams, setTableParams] = useState<TableParams>({
    page: 1,
    limit: 10,
    search: '',
    enquiry_id: undefined,
    is_active: 'Y'
  });
  
  const [totalQuotations, setTotalQuotations] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created_at', desc: true } // Default: newest first
  ]);
  
  const requestIdRef = useRef(0);
  
  // Debounce search
  const debouncedSearch = useDebounce(searchInput, 500);
  
  // Form handling
  const {
    handleSubmit,
    reset,
  } = useForm<QuotationFormData>({
    // Using custom validation instead of Yup schema
    defaultValues: {
      enquiry_id: 0,
      quote_date: '',
      no_of_feet: 0,
      sub_total: 0,
      net_amount: 0,
      remarks: '',
      quotationDetails: []
    }
  });
  
  // Submission protection
  useSubmissionProtection();

  // Load quotations with pagination and filters
  const loadQuotations = useCallback(async (params: TableParams, sortingState?: SortingState) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);

    try {
      const filters: QuotationFilters = {
        page: params.page,
        limit: params.limit,
        search: params.search,
        enquiry_id: params.enquiry_id,
        is_active: params.is_active,
        sort_by: sortingState?.[0]?.id as any,
        sort_order: sortingState?.[0]?.desc ? 'desc' : 'asc'
      };

      const response = await quotationService.getQuotations(filters);

      if (requestId !== requestIdRef.current) return;
      setQuotations(response.quotations || []);
      setTotalQuotations(response.pagination?.total || 0);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (error: any) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error loading quotations:', error);
      showToast.error(error.response?.data?.message || 'Failed to load quotations');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Effect for role-based access control
  useEffect(() => {
    // Check if user has permission to access this page
    if (user && !['ADMIN', 'MANAGER'].includes(user.role)) {
      showToast.error('You do not have permission to access quotation management');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Effect for debounced search
  useEffect(() => {
    setTableParams(prev => ({
      ...prev,
      search: debouncedSearch,
      page: 1
    }));
  }, [debouncedSearch]);

  // Effect to load quotations when table params change
  useEffect(() => {
    loadQuotations(tableParams, sorting);
  }, [tableParams, sorting, loadQuotations]);

  // Effect for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load items when modal opens
  useEffect(() => {
    if (showModal) {
      loadItems();
    }
  }, [showModal]);

  // Async load function for enquiries with pagination
  const loadEnquiryOptions = useCallback(async (search: string, page: number) => {
    try {
      const response = await enquiryService.getEnquiries({
        page: page || 1,
        limit: 50,
        search: search || undefined,
        without_invoice: 'true'
      });
      
      const options = response.enquiries.map(enquiry => ({
        value: enquiry.id.toString(),
        label: `${enquiry.enquiry_no} - ${enquiry.contact_person} - ${enquiry.contact_no} - ${[enquiry.street, enquiry.town, enquiry.district].filter(Boolean).join('|') || 'N/A'}`,
        enquiry: enquiry // Store the full enquiry object for reference
      }));
      
      return {
        options,
        hasMore: page < response.pagination.pages
      };
    } catch (error) {
      console.error('Error loading enquiries:', error);
      return {
        options: [],
        hasMore: false
      };
    }
  }, []);

  // Load items
  const loadItems = async () => {
    try {
      const response = await itemService.getItems({ limit: 1000 });
      setItems(response.data || []);
    } catch (error: any) {
      console.error('Error loading items:', error);
      showToast.error('Failed to load items');
    }
  };

  // Handle enquiry selection
  const handleEnquirySelect = async (enquiry: Enquiry) => {
    setSelectedEnquiry(enquiry);
    setFormData(prev => ({ 
      ...prev, 
      enquiry_id: enquiry.id, 
      no_of_feet: 0,
      sub_total: 0,
      net_amount: 0
    }));
    
    // Clear all items when enquiry changes (both auto and manual/custom)
    setAutoItems([]);
    setManualItems([]);
    setLastCalculatedFeet(0);
    // Reset focused field to clear any Select states
    setFocusedField(null);
    calculateTotals([]);
  };

  // Load slab items based on bore size and feet
  const loadSlabItems = async (boreSizeId: number, feet: number) => {
    try {
      // Use the dedicated API endpoint to get only active slabs for this bore size
      const response = await api.get(`/price-slabs/bore-size/${boreSizeId}`);
      const rawSlabs = response.data.priceSlabs || [];

      // Get bore size type from selected enquiry
      const boreSizeType = selectedEnquiry?.boreSize?.type || 'FEET';

      // Remove duplicate slabs based on from_slab, to_slab, and rate
      const activeSlabs = rawSlabs.filter((slab: any, index: number, array: any[]) => {
        return array.findIndex((s: any) =>
          s.from_slab === slab.from_slab &&
          s.to_slab === slab.to_slab &&
          s.rate === slab.rate
        ) === index;
      });

      // Generate items based on bore size type
      let generatedItems: any[] = [];

      if (boreSizeType === 'FIXED') {
        // For FIXED type, create a single line item with the rate as fixed price
        if (activeSlabs.length > 0) {
          const fixedSlab = activeSlabs[0]; // Use first slab for fixed price
          generatedItems = [{
            sl_no: 1,
            item_name: `${selectedEnquiry?.boreSize?.size} - Fixed Price`,
            particulars: `${selectedEnquiry?.boreSize?.size} - Fixed Price`,
            quantity: 1,
            rate: fixedSlab.rate,
            amount: fixedSlab.rate,
            is_auto_generated: true,
            slab_id: fixedSlab.id,
            from_feet: null,
            to_feet: null
          }];
        }
      } else {
        // For FEET type, calculate based on slabs and footage (existing logic)
        generatedItems = activeSlabs.map((slab: any, index: number) => {
        let quantity = 0;
        let itemName = '';
        
        if (slab.to_slab !== null) {
          const fromFeet = slab.from_slab || 0; // Treat null as 0
          const toFeet = slab.to_slab;
          
          // Calculate quantity for this slab range
          if (feet > fromFeet) {
            if (feet >= toFeet) {
              // Full slab range: user feet covers entire slab
              quantity = toFeet - fromFeet;
            } else {
              // Partial slab range: user feet falls within this slab
              quantity = feet - fromFeet;
            }
          }
          
          // Create descriptive item name
          if (quantity > 0) {
            if (fromFeet === 0) {
              itemName = `Boring ${fromFeet + 1}-${toFeet} feet @ ₹${slab.rate}/ft`;
            } else {
              itemName = `Boring ${fromFeet + 1}-${toFeet} feet @ ₹${slab.rate}/ft`;
            }
          }
        } else {
          // Simple per-foot slab (fallback)
          quantity = feet;
          itemName = `Boring charges @ ₹${slab.rate}/ft`;
        }
        
          return {
            sl_no: index + 1,
            item_name: itemName,
            particulars: itemName,
            quantity: quantity,
            rate: slab.rate,
            amount: quantity * slab.rate,
            is_auto_generated: true,
            slab_id: slab.id,
            from_feet: slab.from_slab || 0,
            to_feet: slab.to_slab
          };
        }).filter((item: any) => item.quantity > 0); // Only include slabs that apply
      }
      
      // Ensure we completely replace auto items, not append to them
      setAutoItems(generatedItems);
      calculateTotals([...generatedItems, ...manualItems]);
      
      if (generatedItems.length > 0) {
        showToast.success(`Generated ${generatedItems.length} active slab items for ${feet} feet`);
        
        // Auto-create new manual row only if there isn't already an empty row
        setTimeout(() => {
          const hasEmptyRow = manualItems.some(item => 
            !item.particulars && item.quantity === 1 && item.rate === 0
          );
          
          if (!hasEmptyRow) {
            // Create new manual item without recalculating totals
            const newItem = {
              sl_no: generatedItems.length + manualItems.length + 1,
              particulars: '',
              quantity: '',
              rate: '',
              amount: 0,
              item_id: null,
              is_auto_generated: false
            };
            
            const updatedManualItems = [...manualItems, newItem];
            setManualItems(updatedManualItems);
            // Don't recalculate totals here - they were already calculated correctly above
          }
        }, 200);
      } else {
        showToast.warning(`No active slabs found for bore size. Please check price slab configuration.`);
      }
    } catch (error: any) {
      console.error('Error loading slab items:', error);
      showToast.error('Failed to load slab items');
    }
  };

  // Add manual item
  const addManualItem = () => {
    const newItem = {
      sl_no: autoItems.length + manualItems.length + 1,
      particulars: '',
      quantity: '',
      rate: '',
      amount: 0,
      item_id: null,
      is_auto_generated: false
    };
    
    const updatedManualItems = [...manualItems, newItem];
    setManualItems(updatedManualItems);
    // Only recalculate totals if we have items with actual amounts
    // Empty manual items shouldn't reset the totals to 0
    if (autoItems.length > 0 || updatedManualItems.some(item => item.amount > 0)) {
      calculateTotals([...autoItems, ...updatedManualItems]);
    }
  };

  // Remove manual item
  const removeManualItem = (index: number) => {
    const updatedManualItems = manualItems.filter((_, i) => i !== index);
    setManualItems(updatedManualItems);
    calculateTotals([...autoItems, ...updatedManualItems]);
  };

  // Update item detail (new signature for form)
  const updateItemDetail = (type: 'auto' | 'manual', index: number, field: string, value: any) => {
    if (type === 'auto') {
      const updatedAutoItems = [...autoItems];
      updatedAutoItems[index] = {
        ...updatedAutoItems[index],
        [field]: value
      };
      
      // Recalculate amount if rate or quantity changed
      if (field === 'rate' || field === 'quantity') {
        const rate = typeof updatedAutoItems[index].rate === 'number' ? updatedAutoItems[index].rate : 0;
        const quantity = typeof updatedAutoItems[index].quantity === 'number' ? updatedAutoItems[index].quantity : 0;
        updatedAutoItems[index].amount = rate * quantity;
      }
      
      setAutoItems(updatedAutoItems);
      calculateTotals([...updatedAutoItems, ...manualItems]);
    } else {
      const updatedManualItems = [...manualItems];
      updatedManualItems[index] = {
        ...updatedManualItems[index],
        [field]: value
      };
      
      // Recalculate amount if rate or quantity changed
      if (field === 'rate' || field === 'quantity') {
        const rate = typeof updatedManualItems[index].rate === 'number' ? updatedManualItems[index].rate : 0;
        const quantity = typeof updatedManualItems[index].quantity === 'number' ? updatedManualItems[index].quantity : 0;
        updatedManualItems[index].amount = rate * quantity;
      }
      
      setManualItems(updatedManualItems);
      calculateTotals([...autoItems, ...updatedManualItems]);
      
      // Auto-add new row if current row is complete
      checkAndAddNewRow(updatedManualItems, index);
    }
  };

  // Check if row is complete and add new row automatically
  const checkAndAddNewRow = (items: any[], index: number) => {
    const item = items[index];
    const isRowComplete = item.particulars && typeof item.quantity === 'number' && item.quantity >= 0 && item.rate > 0;
    const isLastRow = index === items.length - 1;

    if (isRowComplete && isLastRow) {
      // Add new empty row automatically
      const newItem = {
        sl_no: autoItems.length + items.length + 1,
        particulars: '',
        quantity: 1,
        rate: 0,
        amount: 0,
        item_id: undefined,
        is_auto_generated: false
      };

      const updatedItems = [...items, newItem];
      setManualItems(updatedItems);
    }
  };

  // Handle key events (Enter navigation and arrow keys)
  const handleKeyDown = (e: React.KeyboardEvent, type?: 'auto' | 'manual', index?: number, field?: string) => {
    // Prevent up/down arrows on number inputs
    if (e.target instanceof HTMLInputElement && e.target.type === 'number') {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        return;
      }
    }
    
    // Handle Enter key navigation
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // Special handling for feet input
      if (e.target instanceof HTMLInputElement) {
        const placeholder = e.target.placeholder?.toLowerCase();
        
        if (placeholder?.includes('feet')) {
          const feet = parseFloat(e.target.value);
          
          if (feet > 0 && selectedEnquiry?.bore_size_id) {
            // Only regenerate if feet value changed from last calculation
            if (lastCalculatedFeet !== feet) {
              setLastCalculatedFeet(feet);
              loadSlabItems(selectedEnquiry.bore_size_id, feet);
              // Focus on first custom item select after slab generation
              setTimeout(() => {
                const firstCustomSelect = document.querySelector('[data-field="manual-0-particulars"]') as HTMLElement;
                if (firstCustomSelect) {
                  if (firstCustomSelect.tabIndex === 0) {
                    // It's the text display div, click to show Select
                    firstCustomSelect.click();
                    setFocusedField('manual-0-particulars');
                  } else {
                    // It's already a Select input
                    firstCustomSelect.focus();
                  }
                }
              }, 300); // Longer delay to allow slab items to generate
            } else {
              // Same feet value, just focus on first custom item
              setTimeout(() => {
                const firstCustomSelect = document.querySelector('[data-field="manual-0-particulars"]') as HTMLElement;
                if (firstCustomSelect) {
                  if (firstCustomSelect.tabIndex === 0) {
                    firstCustomSelect.click();
                    setFocusedField('manual-0-particulars');
                  } else {
                    firstCustomSelect.focus();
                  }
                }
              }, 100);
            }
          } else if (feet === 0) {
            setAutoItems([]);
            setLastCalculatedFeet(0);
            calculateTotals([...manualItems]);
          }
          return;
        }
      }
      
      // Navigate to next field for manual items
      if (type && index !== undefined && field) {
        navigateToNextField(type, index, field);
      }
    }
    
    // Handle Left/Right Arrow navigation
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      if (type && index !== undefined && field) {
        e.preventDefault();
        
        const fieldOrder = ['particulars', 'quantity', 'rate'];
        const currentFieldIndex = fieldOrder.indexOf(field);
        
        if (e.key === 'ArrowLeft' && currentFieldIndex > 0) {
          // Move to previous field
          const prevField = fieldOrder[currentFieldIndex - 1];
          const prevElement = document.querySelector(`[data-field="${type}-${index}-${prevField}"]`) as HTMLElement;
          if (prevElement) {
            // If it's a div wrapper (for Select), find the input inside
            if (prevElement.tagName === 'DIV') {
              const selectInput = prevElement.querySelector('input');
              if (selectInput) {
                selectInput.focus();
              }
            } else {
              prevElement.focus();
              // If it's an input, select all text
              if (prevElement instanceof HTMLInputElement) {
                prevElement.select();
              }
            }
          }
        } else if (e.key === 'ArrowRight' && currentFieldIndex < fieldOrder.length - 1) {
          // Move to next field
          const nextField = fieldOrder[currentFieldIndex + 1];
          const nextElement = document.querySelector(`[data-field="${type}-${index}-${nextField}"]`) as HTMLElement;
          if (nextElement) {
            // If it's a div wrapper (for Select), find the input inside
            if (nextElement.tagName === 'DIV') {
              const selectInput = nextElement.querySelector('input');
              if (selectInput) {
                selectInput.focus();
              }
            } else {
              nextElement.focus();
              // If it's an input, select all text
              if (nextElement instanceof HTMLInputElement) {
                nextElement.select();
              }
            }
          }
        }
      }
    }
  };
  
  // Helper function to navigate to next field
  const navigateToNextField = (type: string, index: number, field: string) => {
    const nextField = getNextField(field);
    if (nextField) {
      // Focus next field in same row
      const nextElement = document.querySelector(`[data-field="${type}-${index}-${nextField}"]`) as HTMLElement;
      if (nextElement) {
        // If it's a div wrapper (for Select), find the input inside
        if (nextElement.tagName === 'DIV') {
          const selectInput = nextElement.querySelector('input');
          if (selectInput) {
            selectInput.focus();
          }
        } else {
          nextElement.focus();
          // If it's an input, select all text
          if (nextElement instanceof HTMLInputElement) {
            nextElement.select();
          }
        }
        return;
      }
    }
    
    // If no next field in current row, move to next row
    if (type === 'auto') {
      // For auto items, move to next auto item rate field, or first manual item if no more auto items
      const nextAutoIndex = index + 1;
      const nextAutoRateField = document.querySelector(`[data-field="auto-${nextAutoIndex}-rate"]`) as HTMLInputElement;
      if (nextAutoRateField) {
        nextAutoRateField.focus();
        nextAutoRateField.select();
      } else {
        // No more auto items, move to first manual item
        const firstManualElement = document.querySelector(`[data-field="manual-0-particulars"]`) as HTMLElement;
        if (firstManualElement) {
          if (firstManualElement.tabIndex === 0) {
            firstManualElement.click();
            setFocusedField('manual-0-particulars');
          } else {
            firstManualElement.focus();
          }
        }
      }
    } else if (type === 'manual') {
      const nextRowIndex = index + 1;
      const nextRowFirstElement = document.querySelector(`[data-field="manual-${nextRowIndex}-particulars"]`) as HTMLElement;
      if (nextRowFirstElement) {
        // Check if it's the text display div (when not focused)
        if (nextRowFirstElement.tagName === 'DIV' && nextRowFirstElement.tabIndex === 0) {
          // Click or focus to trigger the Select to appear
          nextRowFirstElement.click();
          setFocusedField(`manual-${nextRowIndex}-particulars`);
        } else if (nextRowFirstElement.tagName === 'DIV') {
          // It's a wrapper for Select, find the input inside
          const selectInput = nextRowFirstElement.querySelector('input');
          if (selectInput) {
            selectInput.focus();
          }
        } else {
          nextRowFirstElement.focus();
        }
      } else {
        // Add new row and focus it
        addManualItem();
        setTimeout(() => {
          const newRowElement = document.querySelector(`[data-field="manual-${manualItems.length}-particulars"]`) as HTMLElement;
          if (newRowElement) {
            // Check if it's the text display div (when not focused)
            if (newRowElement.tagName === 'DIV' && newRowElement.tabIndex === 0) {
              // Click or focus to trigger the Select to appear
              newRowElement.click();
              setFocusedField(`manual-${manualItems.length}-particulars`);
            } else if (newRowElement.tagName === 'DIV') {
              // It's a wrapper for Select, find the input inside
              const selectInput = newRowElement.querySelector('input');
              if (selectInput) {
                selectInput.focus();
              }
            } else {
              newRowElement.focus();
            }
          }
        }, 100);
      }
    }
  };

  // Get next field in tab order
  const getNextField = (currentField: string): string | null => {
    const fieldOrder = ['particulars', 'quantity', 'rate'];
    const currentIndex = fieldOrder.indexOf(currentField);
    return currentIndex < fieldOrder.length - 1 ? fieldOrder[currentIndex + 1] : null;
  };


  // Calculate totals
  const calculateTotals = useCallback((allItems: any[]) => {
    const subTotal = allItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    const netAmount = subTotal; // Net amount equals subtotal (no tax)

    setFormData(prev => ({
        ...prev,
        sub_total: subTotal,
        net_amount: netAmount
        // Removed no_of_feet update to preserve user input
      }));
  }, []);

  // Effect for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  

  // Event handlers
  const handleCreate = () => {
    setEditingQuotation(null);
    setSelectedEnquiry(null);
    setAutoItems([]);
    setManualItems([]);
    setFormData({
      enquiry_id: 0,
      quote_date: new Date().toISOString().split('T')[0],
      no_of_feet: 0,
      sub_total: 0,
      net_amount: 0,
      remarks: ''
    });
    reset({
      enquiry_id: 0,
      quote_date: new Date().toISOString().split('T')[0],
      no_of_feet: 0,
      sub_total: 0,
      net_amount: 0,
      remarks: '',
      quotationDetails: []
    });
    setShowModal(true);
  };

  const handleEdit = useCallback(async (quotation: Quotation) => {
    try {
      // Fetch full quotation details
      const fullQuotation = await quotationService.getQuotation(quotation.id);

      // Set editing mode
      setEditingQuotation(fullQuotation);

      // Reset form with quotation data
      reset({
        enquiry_id: fullQuotation.enquiry_id || 0,
        quote_date: fullQuotation.quote_date ? new Date(fullQuotation.quote_date).toISOString().split('T')[0] : '',
        no_of_feet: fullQuotation.no_of_feet || 0,
        remarks: fullQuotation.remarks || ''
      });

      // Update formData with the quotation values
      setFormData(prev => ({
        ...prev,
        enquiry_id: fullQuotation.enquiry_id || 0,
        quote_date: fullQuotation.quote_date ? new Date(fullQuotation.quote_date).toISOString().split('T')[0] : '',
        no_of_feet: fullQuotation.no_of_feet || 0,
        sub_total: fullQuotation.sub_total || 0,
        net_amount: fullQuotation.net_amount || 0,
        remarks: fullQuotation.remarks || ''
      }));
      
      // Set selected enquiry
      if (fullQuotation.enquiry) {
        setSelectedEnquiry(fullQuotation.enquiry as any);
      }
      
      // Process quotation details with helper function
      const { autoGeneratedItems, manualGeneratedItems } = processQuotationDetails(fullQuotation.quotationDetails);
      
      setAutoItems(autoGeneratedItems.map(detail => ({
        particulars: detail.particulars,
        quantity: detail.qty,
        rate: detail.rate,
        amount: detail.amount,
        item_id: detail.item_id,
        item_name: detail.particulars
      })));
      
      // Create manual items array: existing items + 2 empty rows for adding new items
      const manualItemsData = [
        // Existing manual items
        ...manualGeneratedItems.map(item => ({
          particulars: item.particulars || '',
          quantity: item.qty || 0,
          rate: item.rate || 0,
          amount: item.amount || 0,
          item_id: item.item_id || null
        })),
        // Add 2 empty rows for new items
        ...Array(2).fill(null).map(() => ({
          particulars: '',
          quantity: 0,
          rate: 0,
          amount: 0,
          item_id: null
        }))
      ];
      setManualItems(manualItemsData);
      
      // Calculate totals
      calculateTotals([...autoGeneratedItems, ...manualGeneratedItems.map(item => ({
        particulars: item.particulars,
        quantity: item.qty,
        rate: item.rate,
        amount: item.amount,
        item_id: item.item_id
      }))]);
      
      // Set lastCalculatedFeet to prevent regeneration of auto items when feet value changes
      setLastCalculatedFeet(fullQuotation.no_of_feet || 0);
      
      // Open modal
      setShowModal(true);
      
    } catch (error: any) {
      console.error('Error preparing edit:', error);
      showToast.error('Failed to load quotation data for editing');
    }
  }, [reset, calculateTotals]);

  const handleView = useCallback((quotation: Quotation) => {
    // Navigate to quotation view page
    navigate(`/quotations/${quotation.id}`);
  }, [navigate]);

  const handleClone = useCallback(async (quotation: Quotation) => {
    try {
      // Fetch full quotation details
      const fullQuotation = await quotationService.getQuotation(quotation.id);

      // Set cloning mode
      setCloningQuotation(fullQuotation);

      // Reset form with quotation data
      reset({
        enquiry_id: fullQuotation.enquiry_id || 0,
        quote_date: fullQuotation.quote_date ? new Date(fullQuotation.quote_date).toISOString().split('T')[0] : '',
        no_of_feet: fullQuotation.no_of_feet || 0,
        remarks: fullQuotation.remarks || ''
      });

      // Update formData with the quotation values
      setFormData(prev => ({
        ...prev,
        enquiry_id: fullQuotation.enquiry_id || 0,
        quote_date: fullQuotation.quote_date ? new Date(fullQuotation.quote_date).toISOString().split('T')[0] : '',
        no_of_feet: fullQuotation.no_of_feet || 0,
        sub_total: fullQuotation.sub_total || 0,
        net_amount: fullQuotation.net_amount || 0,
        remarks: fullQuotation.remarks || ''
      }));
      
      // Set selected enquiry
      if (fullQuotation.enquiry) {
        setSelectedEnquiry(fullQuotation.enquiry as any);
      }
      
      // Process quotation details with helper function
      const { autoGeneratedItems, manualGeneratedItems } = processQuotationDetails(fullQuotation.quotationDetails);
      
      setAutoItems(autoGeneratedItems.map(detail => ({
        particulars: detail.particulars,
        quantity: detail.qty,
        rate: detail.rate,
        amount: detail.amount,
        item_id: detail.item_id,
        item_name: detail.particulars
      })));
      
      // Create manual items array: existing items + 2 empty rows for adding new items
      const manualItemsData = [
        // Existing manual items
        ...manualGeneratedItems.map(item => ({
          particulars: item.particulars || '',
          quantity: item.qty || 0,
          rate: item.rate || 0,
          amount: item.amount || 0,
          item_id: item.item_id || null
        })),
        // Add 2 empty rows for new items
        ...Array(2).fill(null).map(() => ({
          particulars: '',
          quantity: 0,
          rate: 0,
          amount: 0,
          item_id: null
        }))
      ];
      setManualItems(manualItemsData);
      
      // Calculate totals
      calculateTotals([...autoGeneratedItems, ...manualGeneratedItems.map(item => ({
        particulars: item.particulars,
        quantity: item.qty,
        rate: item.rate,
        amount: item.amount,
        item_id: item.item_id
      }))]);
      
      // Set lastCalculatedFeet to prevent regeneration of auto items when feet value changes
      setLastCalculatedFeet(fullQuotation.no_of_feet || 0);
      
      // Open modal
      setEditingQuotation(null); // Not editing, cloning
      setShowModal(true);
      
    } catch (error: any) {
      console.error('Error preparing clone:', error);
      showToast.error('Failed to load quotation data for cloning');
    }
  }, [reset, calculateTotals]);

  const handleShare = async (quotation: Quotation) => {
    try {
      setSharingQuotationId(quotation.id);
      await quotationService.shareQuotation(quotation.id);
      showToast.success('Quotation shared successfully via WhatsApp');
    } catch (error: any) {
      console.error('Error sharing quotation:', error);
      showToast.error(error.response?.data?.error || 'Failed to share quotation');
    } finally {
      setSharingQuotationId(null);
    }
  };

  const handleDelete = (quotation: Quotation) => {
    setDeleteConfirm({ show: true, quotation });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.quotation) return;

    try {
      setDeleting(true);
      await quotationService.deleteQuotation(deleteConfirm.quotation.id);
      showToast.success('Quotation deleted successfully');
      setDeleteConfirm({ show: false, quotation: null });
      loadQuotations(tableParams, sorting);
    } catch (error: any) {
      console.error('Error deleting quotation:', error);
      showToast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to delete quotation');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingQuotation(null);
    setCloningQuotation(null);
    reset({
      enquiry_id: 0,
      quote_date: '',
      no_of_feet: 0,
      sub_total: 0,
      net_amount: 0,
      remarks: '',
      quotationDetails: []
    });

    // Reset formData as well
    setFormData({
      enquiry_id: 0,
      quote_date: '',
      no_of_feet: 0,
      sub_total: 0,
      net_amount: 0,
      remarks: ''
    });
    
    // Clear other states
    setSelectedEnquiry(null);
    setAutoItems([]);
    setManualItems([...Array(5)].map(() => ({
      particulars: '',
      quantity: 0,
      rate: 0,
      amount: 0,
      item_id: null
    })));
  };

  // Validation function for quotation data
  const validateQuotationData = () => {
    const errors: string[] = [];
    const fieldErrors: {[key: string]: string} = {};
    
    // Clear previous errors
    setFieldErrors({});
    
    // 1. Check for duplicate items in manual items
    const manualItemIds: number[] = [];
    const duplicateIndexes: number[] = [];
    
    manualItems.forEach((item, index) => {
      if (item.item_id && item.particulars) {
        if (manualItemIds.includes(item.item_id)) {
          duplicateIndexes.push(index);
          fieldErrors[`manual-${index}-particulars`] = 'Duplicate item selected';
        } else {
          manualItemIds.push(item.item_id);
        }
      }
    });
    
    if (duplicateIndexes.length > 0) {
      errors.push('Duplicate items are not allowed. Please remove duplicate selections.');
    }
    
    // 2. Validate auto-generated items have rate > 0
    autoItems.forEach((item, index) => {
      if (!item.rate || typeof item.rate !== 'number' || item.rate <= 0) {
        fieldErrors[`auto-${index}-rate`] = 'Rate must be greater than 0';
      }
    });
    
    const invalidAutoItems = autoItems.filter(item => 
      !item.rate || typeof item.rate !== 'number' || item.rate <= 0
    );
    if (invalidAutoItems.length > 0) {
      errors.push('All auto-generated items must have a rate greater than 0.');
    }
    
    // 3. Validate custom items - if item selected, must have valid qty and rate
    manualItems.forEach((item, index) => {
      if (item.particulars && item.particulars.trim() !== '') {
        const hasValidQty = typeof item.quantity === 'number' && item.quantity >= 0;
        const hasValidRate = typeof item.rate === 'number' && item.rate > 0;

        if (!hasValidQty) {
          fieldErrors[`manual-${index}-quantity`] = 'Quantity must be 0 or greater';
        }
        if (!hasValidRate) {
          fieldErrors[`manual-${index}-rate`] = 'Rate must be greater than 0';
        }
      }
    });

    const invalidManualItems = manualItems.filter(item => {
      if (item.particulars && item.particulars.trim() !== '') {
        const hasValidQty = typeof item.quantity === 'number' && item.quantity >= 0;
        const hasValidRate = typeof item.rate === 'number' && item.rate > 0;
        return !hasValidQty || !hasValidRate;
      }
      return false;
    });

    if (invalidManualItems.length > 0) {
      errors.push('Custom items with selected products must have quantity >= 0 and rate greater than 0.');
    }
    
    // 4. Ensure at least one valid item exists
    const validAutoItems = autoItems.filter(item =>
      item.rate && typeof item.rate === 'number' && item.rate > 0
    );
    const validManualItems = manualItems.filter(item =>
      item.particulars && item.particulars.trim() !== '' &&
      typeof item.quantity === 'number' && item.quantity >= 0 &&
      typeof item.rate === 'number' && item.rate > 0
    );

    if (validAutoItems.length === 0 && validManualItems.length === 0) {
      errors.push('At least one valid item is required to create a quotation.');
    }
    
    // Set field errors
    setFieldErrors(fieldErrors);
    
    return errors;
  };

  const onSubmit = async (data: QuotationFormData) => {
    try {
      setSubmitting(true);
      
      // Validate the quotation data
      const validationErrors = validateQuotationData();
      
      if (validationErrors.length > 0) {
        validationErrors.forEach(error => showToast.error(error));
        setSubmitting(false);
        
        // Focus on the first error field
        setTimeout(() => {
          const firstErrorField = document.querySelector('.border-red-500') as HTMLElement;
          if (firstErrorField) {
            firstErrorField.focus();
            // If it's an input, select the text
            if (firstErrorField instanceof HTMLInputElement) {
              firstErrorField.select();
            }
          }
        }, 100);
        
        return;
      }
      
      // Filter out empty manual items and prepare data
      const validManualItems = manualItems.filter(item =>
        item.particulars && item.particulars.trim() !== '' &&
        typeof item.quantity === 'number' && item.quantity >= 0 &&
        typeof item.rate === 'number' && item.rate > 0
      );
      
      const quotationData = {
        ...formData,
        quotationDetails: [
          ...autoItems.map(item => ({
            particulars: item.particulars || item.item_name,
            qty: item.quantity,
            rate: item.rate,
            amount: item.amount,
            item_id: item.item_id || null,
            is_auto_generated: true
          })),
          ...validManualItems.map(item => ({
            particulars: item.particulars,
            qty: item.quantity,
            rate: item.rate,
            amount: item.amount,
            item_id: item.item_id || null,
            is_auto_generated: false
          }))
        ]
      };
      
      if (editingQuotation) {
        await quotationService.updateQuotation(editingQuotation.id, quotationData as any);
        showToast.success('Quotation updated successfully');
      } else if (cloningQuotation) {
        // For cloning, use the cloneQuotation API but we need to update the quotation afterwards
        // First create the clone, then update it with the modified data
        const cloneResult = await quotationService.cloneQuotation(cloningQuotation.id);
        // Now update the cloned quotation with the modified data
        await quotationService.updateQuotation(cloneResult.quotation.id, quotationData as any);
        showToast.success('Quotation cloned successfully');
      } else {
        await quotationService.createQuotation(quotationData as any);
        showToast.success('Quotation created successfully');
      }
      
      handleCloseModal();
      loadQuotations(tableParams, sorting);
    } catch (error: any) {
      console.error('Error saving quotation:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      
      let errorMessage = 'Failed to save quotation';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        errorMessage = error.response.data.errors.map((e: any) => e.msg || e.message).join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showToast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePageChange = (page: number) => {
    setTableParams(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (limit: number) => {
    setTableParams(prev => ({ ...prev, limit, page: 1 }));
  };

  // Define table columns
  const columns = useMemo<ColumnDef<Quotation>[]>(() => [
    {
      id: 'sno',
      header: 'S.No',
      cell: ({ row }) => {
        const index = row.index + 1 + (tableParams.page - 1) * tableParams.limit;
        return <span className="font-medium text-gray-900">{index}</span>;
      },
    },
    {
      accessorKey: 'quote_no',
      header: 'Quote No',
      cell: ({ row }) => (
        <span className="font-medium text-blue-600 whitespace-nowrap">{row.original.quote_no}</span>
      ),
    },
    {
      accessorKey: 'enquiry',
      header: 'Enquiry Details',
      cell: ({ row }) => {
        const enquiry = row.original.enquiry;
        return (
          <div className="w-[160px] text-xs">
            <div className="font-medium text-gray-900 truncate" title={enquiry?.contact_person || '-'}>
              {enquiry?.contact_person || '-'}
            </div>
            <div className="text-gray-600 truncate">{enquiry?.contact_no || '-'}</div>
            <div className="text-gray-600 truncate">{enquiry?.enquiry_no || '-'}</div>
            <div className="text-gray-500 truncate">{enquiry?.boreSize?.size || '-'}</div>
          </div>
        );
      },
    },
    {
      accessorKey: 'quote_date',
      header: 'Quote Date',
      cell: ({ row }) => (
        <span className="text-gray-700 whitespace-nowrap">
          {row.original.quote_date ? new Date(row.original.quote_date).toLocaleDateString() : '-'}
        </span>
      ),
    },
    {
      accessorKey: 'enquiry.service_date',
      header: 'Service Date',
      cell: ({ row }) => (
        <span className="text-gray-700 whitespace-nowrap">
          {row.original.enquiry?.service_date ? new Date(row.original.enquiry.service_date).toLocaleDateString() : '-'}
        </span>
      ),
    },
    {
      accessorKey: 'enquiry.created_at',
      header: 'Enquiry Date',
      cell: ({ row }) => (
        <span className="text-gray-700 whitespace-nowrap">
          {row.original.enquiry?.created_at ? new Date(row.original.enquiry.created_at).toLocaleDateString() : '-'}
        </span>
      ),
    },
    {
      accessorKey: 'net_amount',
      header: 'Net Amount',
      cell: ({ row }) => (
        <span className="font-medium text-gray-900 whitespace-nowrap">
          ₹{(row.original.net_amount || 0).toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      accessorKey: 'remarks',
      header: 'Remarks',
      cell: ({ row }) => (
        <div className="w-[200px]">
          <span
            className="text-gray-700 text-sm break-words line-clamp-3"
            title={row.original.remarks || ''}
          >
            {row.original.remarks || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'version',
      header: 'Version',
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.original.is_active === 'Y' 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          v{row.original.version || 1} {row.original.is_active === 'Y' && '(Active)'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleView(row.original)}
            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
          >
            <EyeIcon className="w-4 h-4" />
          </Button>
          {user?.role === 'ADMIN' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleShare(row.original)}
              className="text-green-600 hover:text-green-800 hover:bg-green-50"
              title="Share via WhatsApp"
              disabled={sharingQuotationId === row.original.id}
            >
              {sharingQuotationId === row.original.id ? (
                <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <ChatBubbleLeftRightIcon className="w-4 h-4" />
              )}
            </Button>
          )}
          {(user?.role === 'ADMIN' || user?.role === 'MANAGER') && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(row.original)}
                className="text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                disabled={row.original.is_active === 'N'}
              >
                <PencilIcon className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleClone(row.original)}
                className="text-purple-600 hover:text-purple-800 hover:bg-purple-50"
              >
                <DocumentDuplicateIcon className="w-4 h-4" />
              </Button>
            </>
          )}
          {user?.role === 'ADMIN' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(row.original)}
              className="text-red-600 hover:text-red-800 hover:bg-red-50"
            >
              <TrashIcon className="w-4 h-4" />
            </Button>
          )}
        </div>
      ),
    },
  ], [user?.role, tableParams.page, tableParams.limit, handleClone, handleEdit, handleView, sharingQuotationId]);

  // Check for authorization before rendering
  if (user && !['ADMIN', 'MANAGER'].includes(user.role)) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Restricted</h2>
            <p className="text-gray-600 mb-4">You do not have permission to access this page.</p>
            <p className="text-sm text-gray-500">Only Administrators and Managers can manage quotations.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex-shrink-0 mb-6">
          {/* Mobile: Compact header with inline actions */}
          <div className="sm:hidden">
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate">Quotations</h1>
                {totalQuotations > 0 && (
                  <p className="text-xs text-gray-500">
                    {totalQuotations} total
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 ml-3">
                <div className="relative">
                  <Button
                    onClick={() => setShowMobileFilters(!showMobileFilters)}
                    size="sm"
                    variant={showMobileFilters ? 'primary' : 'outline'}
                    className="p-2"
                  >
                    {showMobileFilters ? <XMarkIcon className="w-5 h-5" /> : <FunnelIcon className="w-5 h-5" />}
                  </Button>
                  {/* Filter indicator */}
                  {(tableParams.is_active || tableParams.search) && !showMobileFilters && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full"></span>
                  )}
                </div>
                <Button
                  onClick={handleCreate}
                  size="sm"
                  className="p-2"
                >
                  <PlusIcon className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Desktop: Original layout */}
          <div className="hidden sm:flex sm:justify-between sm:items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Quotation Management</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
                Manage quotations and track business proposals
                {totalQuotations > 0 && (
                  <span className="ml-2 text-sm text-gray-500">
                    ({totalQuotations} total quotations)
                  </span>
                )}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleCreate}
                size="lg"
                leftIcon={<PlusIcon className="w-5 h-5" />}
              >
                Create Quotation
              </Button>
            </div>
          </div>
        </div>

        {/* Desktop Filters - Always visible */}
        {!isMobile && (
          <div className="flex-shrink-0 mb-6">
            <div className="bg-white p-4 rounded-2xl shadow-medium border border-gray-200">
              <div className="flex gap-4 items-end">
                {/* Search */}
                <div className="w-64">
                  <Input
                    label="Search"
                    placeholder="Search by quote no, company, contact, or mobile..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>

                {/* Status */}
                <div className="w-44">
                  <Select
                    label="Status"
                    placeholder="All Status"
                    options={[
                      { value: '', label: 'All Status' },
                      { value: 'Y', label: 'Active' },
                      { value: 'N', label: 'Inactive' }
                    ]}
                    value={tableParams.is_active ? { value: tableParams.is_active, label: tableParams.is_active === 'Y' ? 'Active' : 'Inactive' } : null}
                    onChange={(option: any) => setTableParams(prev => ({ ...prev, is_active: option?.value, page: 1 }))}
                    isClearable
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Filter Sidebar */}
        <>
          {isMobile && showMobileFilters && (
            <>
              {/* Backdrop overlay */}
              <div
                className="fixed inset-0 bg-black/50 z-40"
                onClick={() => setShowMobileFilters(false)}
              />

              {/* Filter sidebar */}
              <div
                className="fixed top-0 right-0 h-full w-80 bg-white z-50 shadow-xl flex flex-col"
              >
                {/* Sidebar header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* Filter content */}
                <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                  {/* Search */}
                  <Input
                    label="Search"
                    placeholder="Search by quote no, company, contact, or mobile..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />

                  {/* Status */}
                  <Select
                    label="Status"
                    placeholder="All Status"
                    options={[
                      { value: '', label: 'All Status' },
                      { value: 'Y', label: 'Active' },
                      { value: 'N', label: 'Inactive' }
                    ]}
                    value={tableParams.is_active ? { value: tableParams.is_active, label: tableParams.is_active === 'Y' ? 'Active' : 'Inactive' } : null}
                    onChange={(option: any) => setTableParams(prev => ({ ...prev, is_active: option?.value, page: 1 }))}
                    isClearable
                    usePortal={false}
                  />
                </div>

                {/* Sidebar footer with action buttons */}
                <div className="p-4 border-t border-gray-200 space-y-3">
                  <Button
                    onClick={() => {
                      setSearchInput('');
                      setTableParams(prev => ({
                        ...prev,
                        search: '',
                        is_active: 'Y',
                        page: 1
                      }));
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    Clear All Filters
                  </Button>
                  <Button
                    onClick={() => setShowMobileFilters(false)}
                    className="w-full"
                  >
                    Apply Filters
                  </Button>
                </div>
              </div>
            </>
          )}
        </>

        {/* Data Table for Desktop and Large Tablets */}
        {!isMobile ? (
          <div className="flex-1 bg-white rounded-2xl shadow-medium border border-gray-200 flex flex-col min-h-[500px]">
            <div className="flex-1 overflow-hidden">
              <DataTable
                data={quotations}
                columns={columns}
                loading={loading}
                searchable={false}
                pageSize={tableParams.limit}
                showPagination={false}
                sorting={sorting}
                onSortingChange={setSorting}
              />
            </div>
            
            {/* Custom Pagination for Desktop */}
            <div className="flex-shrink-0 px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-white">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 lg:gap-4">
                <div className="text-xs sm:text-sm text-gray-700 whitespace-nowrap order-2 lg:order-1">
                  Showing {((tableParams.page - 1) * tableParams.limit) + 1} to{' '}
                  {Math.min(tableParams.page * tableParams.limit, totalQuotations)} of{' '}
                  {totalQuotations} results
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full lg:w-auto order-1 lg:order-2">
                  <div className="w-full sm:w-auto">
                    <Select
                      options={[
                        { value: '5', label: window.innerWidth < 640 ? '5' : '5 per page' },
                        { value: '10', label: window.innerWidth < 640 ? '10' : '10 per page' },
                        { value: '20', label: window.innerWidth < 640 ? '20' : '20 per page' },
                        { value: '50', label: window.innerWidth < 640 ? '50' : '50 per page' }
                      ]}
                      value={{ value: tableParams.limit.toString(), label: window.innerWidth < 640 ? tableParams.limit.toString() : `${tableParams.limit} per page` }}
                      onChange={(option: any) => handlePageSizeChange(Number(option?.value))}
                      usePortal={false}
                      menuPlacement="top"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePageChange(tableParams.page - 1)}
                      disabled={tableParams.page === 1}
                    >
                      <span className="hidden sm:inline">Previous</span>
                      <span className="sm:hidden">Prev</span>
                    </Button>
                    <div className="flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm text-gray-700 bg-white border border-gray-200 rounded-lg min-w-[80px] sm:min-w-[120px] justify-center">
                      <span className="hidden sm:inline">Page {tableParams.page} of {totalPages}</span>
                      <span className="sm:hidden">{tableParams.page}/{totalPages}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePageChange(tableParams.page + 1)}
                      disabled={tableParams.page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Mobile Cards View
          <div className="flex-1 min-h-0 flex flex-col gap-4 relative">
            {loading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-2xl">
                <div className="flex flex-col items-center p-6 bg-white rounded-2xl shadow-strong border border-gray-200">
                  <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                  <p className="text-gray-600 text-sm font-medium mt-3">Loading quotations...</p>
                </div>
              </div>
            )}
            <div className="flex-1 min-h-0 overflow-auto bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="divide-y divide-gray-100">
                {quotations.map((quotation, index) => (
                  <div
                    key={quotation.id}
                    className="bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors"
                  >
                    <div className="px-4 py-3">
                      <div className="flex items-start space-x-3">
                        {/* Compact number indicator */}
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0 mt-0.5">
                          {((tableParams.page - 1) * tableParams.limit) + index + 1}
                        </div>

                        <div className="flex-1 min-w-0">
                          {/* Main content */}
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              {/* Primary info */}
                              <div className="font-medium text-gray-900 truncate">
                                {quotation.quote_no}
                              </div>

                              {/* Secondary info line */}
                              <div className="flex items-center space-x-2 text-xs text-gray-500 mt-0.5">
                                <span className="truncate">
                                  {quotation.enquiry?.company_name || quotation.enquiry?.contact_person || 'N/A'}
                                </span>
                                {quotation.enquiry?.boreSize?.size && (
                                  <>
                                    <span>•</span>
                                    <span>{quotation.enquiry.boreSize.size}</span>
                                  </>
                                )}
                              </div>

                              {/* Amount and date row */}
                              <div className="flex items-center space-x-3 mt-1">
                                <div className="text-sm font-medium text-gray-900">
                                  ₹{(quotation.net_amount || 0).toLocaleString('en-IN')}
                                </div>
                                {quotation.quote_date && (
                                  <>
                                    <span className="text-gray-300">•</span>
                                    <span className="text-xs text-gray-600">
                                      {new Date(quotation.quote_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </span>
                                  </>
                                )}
                              </div>

                              {/* Remarks */}
                              {quotation.remarks && (
                                <div className="text-xs text-gray-600 mt-1 truncate">
                                  {quotation.remarks}
                                </div>
                              )}

                              {/* Status badges */}
                              <div className="flex items-center gap-2 mt-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  quotation.is_active === 'Y'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  v{quotation.version || 1}
                                </span>
                              </div>
                            </div>

                            {/* Action menu */}
                            <div className="relative ml-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowActionMenu(showActionMenu === quotation.id ? null : quotation.id);
                                }}
                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                              >
                                <EllipsisVerticalIcon className="w-4 h-4 text-gray-400" />
                              </button>

                              {showActionMenu === quotation.id && (
                                <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                                  <button
                                    onClick={() => {
                                      handleView(quotation);
                                      setShowActionMenu(null);
                                    }}
                                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                                  >
                                    <EyeIcon className="w-3 h-3 mr-2" />
                                    View
                                  </button>
                                  {user?.role === 'ADMIN' && (
                                    <button
                                      onClick={() => {
                                        handleShare(quotation);
                                        setShowActionMenu(null);
                                      }}
                                      disabled={sharingQuotationId === quotation.id}
                                      className="w-full px-3 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center disabled:opacity-50"
                                    >
                                      {sharingQuotationId === quotation.id ? (
                                        <div className="w-3 h-3 mr-2 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                                      ) : (
                                        <ChatBubbleLeftRightIcon className="w-3 h-3 mr-2" />
                                      )}
                                      Share WhatsApp
                                    </button>
                                  )}
                                  {(user?.role === 'ADMIN' || user?.role === 'MANAGER') && (
                                    <>
                                      <button
                                        onClick={() => {
                                          handleEdit(quotation);
                                          setShowActionMenu(null);
                                        }}
                                        disabled={quotation.is_active === 'N'}
                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center disabled:opacity-50"
                                      >
                                        <PencilIcon className="w-3 h-3 mr-2" />
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => {
                                          handleClone(quotation);
                                          setShowActionMenu(null);
                                        }}
                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                                      >
                                        <DocumentDuplicateIcon className="w-3 h-3 mr-2" />
                                        Clone
                                      </button>
                                    </>
                                  )}
                                  {user?.role === 'ADMIN' && (
                                    <button
                                      onClick={() => {
                                        handleDelete(quotation);
                                        setShowActionMenu(null);
                                      }}
                                      className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                                    >
                                      <TrashIcon className="w-3 h-3 mr-2" />
                                      Delete
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {quotations.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <div className="text-gray-500 text-lg">No quotations found</div>
                    <div className="text-gray-400 text-sm mt-2">Try adjusting your search or filter criteria</div>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Pagination */}
            <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 bg-white rounded-b-2xl">
              <div className="flex items-center justify-between gap-2">
                {/* Per page selector on left */}
                <div className="w-20">
                  <Select
                    options={[
                      { value: '5', label: '5' },
                      { value: '10', label: '10' },
                      { value: '20', label: '20' },
                      { value: '50', label: '50' }
                    ]}
                    value={{ value: tableParams.limit.toString(), label: tableParams.limit.toString() }}
                    onChange={(option: any) => handlePageSizeChange(Number(option?.value))}
                    usePortal={false}
                    menuPlacement="top"
                    isSearchable={false}
                  />
                </div>

                {/* Navigation on right */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(tableParams.page - 1)}
                    disabled={tableParams.page === 1}
                    className="px-3 h-8"
                  >
                    ←
                  </Button>

                  <div className="text-xs text-gray-600 bg-gray-100 px-3 py-1 rounded-full whitespace-nowrap">
                    Page {tableParams.page} of {totalPages}
                  </div>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(tableParams.page + 1)}
                    disabled={tableParams.page === totalPages}
                    className="px-3 h-8"
                  >
                    →
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={handleCloseModal}
          title={editingQuotation ? 'Edit Quotation' : cloningQuotation ? 'Clone Quotation' : 'Create New Quotation'}
          size={isMobile ? "full" : "2xl"}
        >
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3 sm:gap-4">
            {/* Form Content */}
            <div className="flex flex-col gap-3 sm:gap-4">
              {/* Enquiry Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Enquiry *
              </label>
              <Select
                value={selectedEnquiry ? {
                  value: selectedEnquiry.id.toString(),
                  label: `${selectedEnquiry.enquiry_no} - ${selectedEnquiry.contact_person} - ${selectedEnquiry.contact_no} - ${[selectedEnquiry.street, selectedEnquiry.town, selectedEnquiry.district].filter(Boolean).join('|') || 'N/A'}`
                } : null}
                onChange={(option: any) => {
                  if (option && option.value) {
                    // The enquiry object is stored in the option when using async loading
                    const enquiry = option.enquiry || enquiries.find(enq => enq.id === parseInt(option.value));
                    if (enquiry) {
                      handleEnquirySelect(enquiry);
                      // Focus on Add Feet input after enquiry selection
                      setTimeout(() => {
                        const feetInput = document.querySelector('input[placeholder*="feet"]') as HTMLInputElement;
                        if (feetInput) {
                          feetInput.focus();
                          feetInput.select();
                        }
                      }, 100);
                    }
                  } else {
                    setSelectedEnquiry(null);
                    setFormData(prev => ({ ...prev, enquiry_id: 0 }));
                  }
                }}
                required
                isDisabled={submitting}
                isSearchable={true}
                isClearable={true}
                placeholder="Select or type to search enquiry..."
                async
                asyncConfig={{
                  loadOptions: loadEnquiryOptions,
                  pageSize: 50,
                  debounceTimeout: 300
                }}
              />
            </div>

            {/* Enquiry Details */}
            {selectedEnquiry && (
              <div className="bg-blue-50 p-2 rounded-lg">
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-medium text-blue-700 whitespace-nowrap">Service Date:</span>
                    <span className="text-xs text-blue-900 bg-white px-2 py-1 rounded border flex-1 sm:flex-none truncate">
                      {selectedEnquiry.service_date ? new Date(selectedEnquiry.service_date).toLocaleDateString() : 'Not specified'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-medium text-blue-700 whitespace-nowrap">Bore Size:</span>
                    <span className="text-xs text-blue-900 bg-white px-2 py-1 rounded border flex-1 sm:flex-none sm:min-w-[100px] truncate">
                      {selectedEnquiry.boreSize?.size || 'Not specified'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <label className="text-xs font-medium text-blue-700 whitespace-nowrap">Add Feet *</label>
                    <div className="flex items-center gap-2 flex-1 sm:flex-none">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        inputMode="numeric"
                        value={formData.no_of_feet}
                        onChange={(e) => {
                          const feet = parseFloat(e.target.value) || 0;
                          setFormData(prev => ({ ...prev, no_of_feet: feet }));
                          // Reset lastCalculatedFeet when user manually changes the value
                          // This allows regeneration of auto items with new feet value
                          if (feet !== lastCalculatedFeet) {
                            setLastCalculatedFeet(0);
                          }
                        }}
                        onKeyDown={(e) => handleKeyDown(e)}
                        className="w-full sm:w-28 px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter feet"
                        required
                        disabled={submitting}
                      />
                      {/* Mobile Generate Button */}
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          const feet = formData.no_of_feet;
                          if (feet > 0 && selectedEnquiry?.bore_size_id) {
                            if (lastCalculatedFeet !== feet) {
                              setLastCalculatedFeet(feet);
                              loadSlabItems(selectedEnquiry.bore_size_id, feet);
                            }
                          }
                        }}
                        disabled={submitting || !formData.no_of_feet || formData.no_of_feet <= 0 || !selectedEnquiry?.bore_size_id}
                        className="sm:hidden flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Generate
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Combined Items Table */}
            {(autoItems.length > 0 || manualItems.length > 0) && (
              <div>
                <div className="mb-2">
                  <h3 className="text-sm sm:text-base font-medium text-gray-900">
                    Quotation Items
                  </h3>
                  {autoItems.length > 0 && (
                    <span className="text-xs text-blue-600 block">
                      ({formData.no_of_feet} feet - Slab rates editable)
                    </span>
                  )}
                </div>

                <div className="border border-gray-200 rounded-lg bg-white">
                  {/* Desktop Table Header */}
                  <div className="hidden sm:block bg-gray-50 border-b">
                    <div className="grid grid-cols-12 gap-3 px-3 py-3">
                      <div className="col-span-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item / Description
                      </div>
                      <div className="col-span-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </div>
                      <div className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rate
                      </div>
                      <div className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">
                        Amount
                      </div>
                      <div className="col-span-1 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                        Action
                      </div>
                    </div>
                  </div>

                  <div>
                    {/* Mobile Cards Container */}
                    <div className="block sm:hidden divide-y divide-gray-200">
                      {/* Auto-Generated Items Mobile Cards */}
                      {autoItems.map((item, index) => (
                        <div key={`auto-${index}`} className="p-2.5 bg-blue-50 border-l-3 border-blue-400">
                          <div className="flex items-start gap-2 mb-1.5">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0">
                              Auto
                            </span>
                            <p className="text-xs text-gray-900 leading-tight flex-1 min-w-0">
                              {item.item_name || item.particulars}
                            </p>
                          </div>
                          <div className="grid grid-cols-3 gap-1.5">
                            <div>
                              <label className="text-xs text-gray-600 block mb-0.5">Qty</label>
                              <p className="text-sm text-gray-900 bg-white px-1.5 py-1 rounded border text-center">
                                {item.quantity}
                              </p>
                            </div>
                            <div>
                              <label className="text-xs text-gray-600 block mb-0.5">Rate</label>
                              <input
                                type="number"
                                step="0.01"
                                inputMode="numeric"
                                value={item.rate}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  const value = parseFloat(inputValue);
                                  setFieldErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors[`auto-${index}-rate`];
                                    return newErrors;
                                  });
                                  updateItemDetail('auto', index, 'rate', inputValue === '' || isNaN(value) ? '' : value);
                                }}
                                className={`w-full px-1.5 py-1 text-sm border rounded focus:outline-none focus:ring-1 ${
                                  fieldErrors[`auto-${index}-rate`]
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 focus:ring-blue-500'
                                }`}
                                disabled={submitting}
                              />
                              {fieldErrors[`auto-${index}-rate`] && (
                                <div className="text-xs text-red-600 mt-0.5">
                                  {fieldErrors[`auto-${index}-rate`]}
                                </div>
                              )}
                            </div>
                            <div>
                              <label className="text-xs text-gray-600 block mb-0.5">Amount</label>
                              <p className="text-xs font-semibold text-gray-900 bg-gray-50 px-1.5 py-1 rounded border text-right">
                                ₹{((typeof item.quantity === 'number' ? item.quantity : 0) * (typeof item.rate === 'number' ? item.rate : 0)).toFixed(0)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Manual Items Mobile Cards */}
                      {manualItems.map((item, index) => (
                        <div key={`manual-${index}`} className="p-2.5 bg-white">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs text-gray-500">Custom #{index + 1}</span>
                            <button
                              type="button"
                              onClick={() => removeManualItem(index)}
                              className="p-0.5 text-red-600 hover:text-red-800"
                              disabled={submitting}
                            >
                              <TrashIcon className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <label className="text-xs text-gray-600 block mb-0.5">Item *</label>
                              {focusedField === `manual-${index}-particulars` ? (
                                <div>
                                  <Select
                                    value={item.item_id ? item.item_id.toString() : ''}
                                    onChange={(option: any) => {
                                      if (option && option.value) {
                                        const selectedItem = items.find(i => i.id === parseInt(option.value));
                                        if (selectedItem) {
                                          const updatedManualItems = [...manualItems];
                                          updatedManualItems[index] = {
                                            ...updatedManualItems[index],
                                            item_id: selectedItem.id,
                                            particulars: selectedItem.name,
                                            rate: selectedItem.rate,
                                            amount: updatedManualItems[index].quantity * selectedItem.rate
                                          };
                                          setManualItems(updatedManualItems);
                                          calculateTotals([...autoItems, ...updatedManualItems]);
                                          setTimeout(() => setFocusedField(null), 100);
                                        }
                                      } else {
                                        updateItemDetail('manual', index, 'item_id', null);
                                        updateItemDetail('manual', index, 'particulars', '');
                                        updateItemDetail('manual', index, 'rate', 0);
                                      }
                                    }}
                                    onBlur={() => setTimeout(() => setFocusedField(null), 200)}
                                    isDisabled={submitting}
                                    isSearchable={true}
                                    isClearable={true}
                                    placeholder="Select item..."
                                    options={items.map((masterItem) => ({
                                      value: masterItem.id.toString(),
                                      label: `${masterItem.name} - ₹${masterItem.rate}`
                                    }))}
                                    autoFocus={true}
                                  />
                                </div>
                              ) : (
                                <input
                                  type="text"
                                  value={item.particulars}
                                  onChange={(e) => {
                                    setFieldErrors(prev => {
                                      const newErrors = { ...prev };
                                      delete newErrors[`manual-${index}-particulars`];
                                      return newErrors;
                                    });
                                    updateItemDetail('manual', index, 'particulars', e.target.value);
                                  }}
                                  onFocus={() => setFocusedField(`manual-${index}-particulars`)}
                                  className={`w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 ${
                                    fieldErrors[`manual-${index}-particulars`]
                                      ? 'border-red-500 focus:ring-red-500'
                                      : 'border-gray-300 focus:ring-blue-500'
                                  }`}
                                  placeholder="Item description"
                                  disabled={submitting}
                                />
                              )}
                              {fieldErrors[`manual-${index}-particulars`] && (
                                <div className="text-xs text-red-600 mt-0.5">
                                  {fieldErrors[`manual-${index}-particulars`]}
                                </div>
                              )}
                            </div>
                            <div className="grid grid-cols-3 gap-1.5">
                              <div>
                                <label className="text-xs text-gray-600 block mb-0.5">Qty *</label>
                                <input
                                  type="number"
                                  inputMode="numeric"
                                  step="0.01"
                                  min="0"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    setFieldErrors(prev => {
                                      const newErrors = { ...prev };
                                      delete newErrors[`manual-${index}-quantity`];
                                      return newErrors;
                                    });
                                    updateItemDetail('manual', index, 'quantity', parseFloat(e.target.value) || 0);
                                  }}
                                  className={`w-full px-1.5 py-1 text-sm border rounded focus:outline-none focus:ring-1 ${
                                    fieldErrors[`manual-${index}-quantity`]
                                      ? 'border-red-500 focus:ring-red-500'
                                      : 'border-gray-300 focus:ring-blue-500'
                                  }`}
                                  disabled={submitting}
                                />
                                {fieldErrors[`manual-${index}-quantity`] && (
                                  <div className="text-xs text-red-600 mt-0.5">
                                    {fieldErrors[`manual-${index}-quantity`]}
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="text-xs text-gray-600 block mb-0.5">Rate *</label>
                                <input
                                  type="number"
                                  inputMode="numeric"
                                  step="0.01"
                                  min="0"
                                  value={item.rate}
                                  onChange={(e) => {
                                    const inputValue = e.target.value;
                                    const value = parseFloat(inputValue);
                                    setFieldErrors(prev => {
                                      const newErrors = { ...prev };
                                      delete newErrors[`manual-${index}-rate`];
                                      return newErrors;
                                    });
                                    updateItemDetail('manual', index, 'rate', inputValue === '' || isNaN(value) ? '' : value);
                                  }}
                                  className={`w-full px-1.5 py-1 text-sm border rounded focus:outline-none focus:ring-1 ${
                                    fieldErrors[`manual-${index}-rate`]
                                      ? 'border-red-500 focus:ring-red-500'
                                      : 'border-gray-300 focus:ring-blue-500'
                                  }`}
                                  disabled={submitting}
                                />
                                {fieldErrors[`manual-${index}-rate`] && (
                                  <div className="text-xs text-red-600 mt-0.5">
                                    {fieldErrors[`manual-${index}-rate`]}
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="text-xs text-gray-600 block mb-0.5">Amount</label>
                                <p className="text-xs font-semibold text-gray-900 bg-gray-50 px-1.5 py-1 rounded border text-right">
                                  ₹{((typeof item.quantity === 'number' ? item.quantity : 0) * (typeof item.rate === 'number' ? item.rate : 0)).toFixed(0)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Desktop Grid Layout */}
                    <div className="hidden sm:block divide-y divide-gray-200">
                      {/* Auto-Generated Slab Items First */}
                      {autoItems.map((item, index) => (
                        <div key={`auto-${index}`} className="grid grid-cols-12 gap-3 px-3 py-3 bg-blue-50 border-l-4 border-blue-400">
                          <div className="col-span-6 text-sm text-gray-900">
                            <div className="flex items-center">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                                Auto
                              </span>
                              <span className="truncate">{item.item_name || item.particulars}</span>
                            </div>
                          </div>
                          <div className="col-span-1 text-sm text-gray-900 text-center">
                            {item.quantity}
                          </div>
                          <div className="col-span-2">
                            <div>
                              <input
                                type="number"
                                inputMode="numeric"
                                step="0.01"
                                value={item.rate}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  const value = parseFloat(inputValue);
                                  // Clear field error when user starts typing
                                  setFieldErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors[`auto-${index}-rate`];
                                    return newErrors;
                                  });
                                  // If input is empty or NaN, keep it empty; otherwise use the value
                                  updateItemDetail('auto', index, 'rate', inputValue === '' || isNaN(value) ? '' : value);
                                }}
                                onKeyDown={(e) => handleKeyDown(e, 'auto', index, 'rate')}
                                data-field={`auto-${index}-rate`}
                                className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 ${
                                  fieldErrors[`auto-${index}-rate`] 
                                    ? 'border-red-500 focus:ring-red-500' 
                                    : 'border-gray-300 focus:ring-blue-500'
                                }`}
                                disabled={submitting}
                              />
                              {fieldErrors[`auto-${index}-rate`] && (
                                <div className="text-xs text-red-600 mt-1">
                                  {fieldErrors[`auto-${index}-rate`]}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-span-2 text-sm font-semibold text-gray-900 text-right">
                            ₹{((typeof item.quantity === 'number' ? item.quantity : 0) * (typeof item.rate === 'number' ? item.rate : 0)).toFixed(0)}
                          </div>
                          <div className="col-span-1 text-center text-sm text-gray-500">
                            -
                          </div>
                        </div>
                      ))}

                      {/* Manual/Custom Items Below */}
                      {manualItems.map((item, index) => (
                        <div key={`manual-${index}`} className="grid grid-cols-12 gap-3 px-3 py-3 bg-white border-l-4 border-gray-300">
                          <div className="col-span-6">
                            {focusedField === `manual-${index}-particulars` ? (
                              <div 
                                onKeyDown={(e) => {
                                  // Handle Enter and Arrow keys for Select
                                  if (e.key === 'Enter' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                                    handleKeyDown(e, 'manual', index, 'particulars');
                                  }
                                }}
                                data-field={`manual-${index}-particulars`}
                              >
                                <Select
                                  value={item.item_id ? item.item_id.toString() : ''}
                                  onChange={(option: any) => {
                                    if (option && option.value) {
                                      const selectedItem = items.find(i => i.id === parseInt(option.value));
                                      if (selectedItem) {
                                        // Update all fields at once to prevent state conflicts
                                        const updatedManualItems = [...manualItems];
                                        updatedManualItems[index] = {
                                          ...updatedManualItems[index],
                                          item_id: selectedItem.id,
                                          particulars: selectedItem.name,
                                          rate: selectedItem.rate,
                                          amount: updatedManualItems[index].quantity * selectedItem.rate
                                        };
                                        setManualItems(updatedManualItems);
                                        calculateTotals([...autoItems, ...updatedManualItems]);
                                        // Remove focus after selection
                                        setTimeout(() => {
                                          setFocusedField(null);
                                          // Focus quantity field
                                          const qtyInput = document.querySelector(`[data-field="manual-${index}-quantity"]`) as HTMLInputElement;
                                          if (qtyInput) {
                                            qtyInput.focus();
                                            qtyInput.select();
                                          }
                                        }, 100);
                                      }
                                    } else {
                                      // Clear selection
                                      updateItemDetail('manual', index, 'item_id', null);
                                      updateItemDetail('manual', index, 'particulars', '');
                                      updateItemDetail('manual', index, 'rate', 0);
                                    }
                                  }}
                                  onBlur={() => {
                                    setTimeout(() => setFocusedField(null), 200);
                                  }}
                                  isDisabled={submitting}
                                  isSearchable={true}
                                  isClearable={true}
                                  placeholder="Select or type to search items..."
                                  options={items.map((masterItem) => ({
                                    value: masterItem.id.toString(),
                                    label: `${masterItem.name} - ₹${masterItem.rate}`
                                  }))}
                                  autoFocus={true}
                                />
                              </div>
                            ) : (
                              <div>
                                <div
                                  className={`w-full px-2 py-1 text-sm border rounded cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring-1 ${
                                    fieldErrors[`manual-${index}-particulars`] 
                                      ? 'border-red-500 focus:ring-red-500' 
                                      : 'border-gray-300 focus:ring-blue-500'
                                  }`}
                                  onClick={() => {
                                    // Clear error when field is clicked
                                    setFieldErrors(prev => {
                                      const newErrors = { ...prev };
                                      delete newErrors[`manual-${index}-particulars`];
                                      return newErrors;
                                    });
                                    setFocusedField(`manual-${index}-particulars`);
                                  }}
                                  onFocus={() => setFocusedField(`manual-${index}-particulars`)}
                                  tabIndex={0}
                                  data-field={`manual-${index}-particulars`}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setFocusedField(`manual-${index}-particulars`);
                                    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                                      handleKeyDown(e, 'manual', index, 'particulars');
                                    }
                                  }}
                                >
                                  {item.particulars || <span className="text-gray-400">Select or type to search items...</span>}
                                </div>
                                {fieldErrors[`manual-${index}-particulars`] && (
                                  <div className="text-xs text-red-600 mt-1">
                                    {fieldErrors[`manual-${index}-particulars`]}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="col-span-1">
                            <div>
                              <input
                                type="number"
                                inputMode="numeric"
                                step="0.01"
                                min="0"
                                value={item.quantity}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value);
                                  // Clear field error when user starts typing
                                  setFieldErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors[`manual-${index}-quantity`];
                                    return newErrors;
                                  });
                                  // Allow 0 quantity for custom/manual items
                                  updateItemDetail('manual', index, 'quantity', isNaN(value) ? '' : value);
                                }}
                                onKeyDown={(e) => handleKeyDown(e, 'manual', index, 'quantity')}
                                data-field={`manual-${index}-quantity`}
                                className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 ${
                                  fieldErrors[`manual-${index}-quantity`] 
                                    ? 'border-red-500 focus:ring-red-500' 
                                    : 'border-gray-300 focus:ring-blue-500'
                                }`}
                                placeholder="Qty"
                                disabled={submitting}
                              />
                              {fieldErrors[`manual-${index}-quantity`] && (
                                <div className="text-xs text-red-600 mt-1">
                                  {fieldErrors[`manual-${index}-quantity`]}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-span-2">
                            <div>
                              <input
                                type="number"
                                inputMode="numeric"
                                step="0.01"
                                min="0"
                                value={item.rate}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  const value = parseFloat(inputValue);
                                  // Clear field error when user starts typing
                                  setFieldErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors[`manual-${index}-rate`];
                                    return newErrors;
                                  });
                                  // If input is empty or NaN, keep it empty; otherwise use the value
                                  updateItemDetail('manual', index, 'rate', inputValue === '' || isNaN(value) ? '' : value);
                                }}
                                onKeyDown={(e) => handleKeyDown(e, 'manual', index, 'rate')}
                                data-field={`manual-${index}-rate`}
                                className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 ${
                                  fieldErrors[`manual-${index}-rate`] 
                                    ? 'border-red-500 focus:ring-red-500' 
                                    : 'border-gray-300 focus:ring-blue-500'
                                }`}
                                placeholder="Rate"
                                disabled={submitting}
                              />
                              {fieldErrors[`manual-${index}-rate`] && (
                                <div className="text-xs text-red-600 mt-1">
                                  {fieldErrors[`manual-${index}-rate`]}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-span-2 text-sm text-gray-900 text-right">
                            ₹{((typeof item.quantity === 'number' ? item.quantity : 0) * (typeof item.rate === 'number' ? item.rate : 0)).toFixed(0)}
                          </div>
                          <div className="col-span-1 flex justify-center">
                            <button
                              type="button"
                              onClick={() => removeManualItem(index)}
                              className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                              disabled={submitting}
                              title="Remove item"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Add Item button - bottom of list */}
                  <div className="border-t border-gray-200 p-2">
                    <button
                      type="button"
                      onClick={() => addManualItem()}
                      disabled={submitting}
                      className="w-full flex items-center justify-center gap-2 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add Custom Item
                    </button>
                  </div>
                </div>
              </div>
            )}
            </div>

            {/* Totals - natural flow */}
            {(autoItems.length > 0 || manualItems.length > 0) && (
              <div className="bg-gray-50 p-3 rounded-lg border">
                <div className="flex items-center justify-between sm:justify-end sm:gap-6">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="text-xs sm:text-sm font-medium text-gray-700">Subtotal:</span>
                    <span className="text-xs sm:text-sm font-semibold text-gray-900">₹{formData.sub_total.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 sm:border-l sm:pl-6">
                    <span className="text-sm font-semibold text-gray-900">Total:</span>
                    <span className="text-base sm:text-lg font-bold text-blue-600">₹{formData.net_amount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Remarks */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Remarks
              </label>
              <textarea
                value={formData.remarks || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder="Enter any remarks or notes..."
                disabled={submitting}
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 sm:gap-3 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                disabled={submitting}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1"
              >
                <XMarkIcon className="w-4 h-4" />
                Cancel
              </Button>
              <Button
                type="submit"
                loading={submitting}
                disabled={!selectedEnquiry || !formData.no_of_feet || formData.no_of_feet <= 0 || (autoItems.length === 0 && manualItems.length === 0)}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1"
              >
                <CheckIcon className="w-4 h-4" />
                {editingQuotation ? 'Update' : cloningQuotation ? 'Clone' : 'Create'} Quotation
              </Button>
            </div>
          </form>
        </Modal>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ show: false, quotation: null })}
          onConfirm={confirmDelete}
          title="Delete Quotation"
          message={`Are you sure you want to delete the quotation "${deleteConfirm.quotation?.quote_no}"? This action cannot be undone.`}
          confirmText="Delete"
          variant="danger"
          loading={deleting}
        />

        {/* Loading Overlay for Share Action */}
        {sharingQuotationId !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 shadow-xl flex flex-col items-center space-y-4">
              <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-900">Sharing Quotation</p>
                <p className="text-sm text-gray-600 mt-1">Sending via WhatsApp...</p>
              </div>
            </div>
          </div>
        )}

      </div>
    </MainLayout>
  );
};

export default QuotationManagement;